import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Pencil, Target, BarChart3, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GAME_TYPES, getGameTypeLabel } from '@low6-pickem/sports-config';
import { ENABLED_SPORTS, isGameTypeEnabled } from '@/lib/platform-filters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/assets/Icon';
import { useGetLivePickemsQuery } from '@/api/live-scoring/getLivePickems';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useCurrentAdminUser } from '@/hooks/useCurrentAdminUser';
import { getSportLogoUrl } from '@low6-pickem/sports-config';
import { Skeleton } from '@/components/ui/skeleton';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LivePickem } from '@/api/live-scoring/getLivePickems';

export const Route = createFileRoute('/dashboard/live-scoring/')({
  component: LiveScoringPage,
});

function LiveScoringPage() {
  const navigate = useNavigate();
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [gameFilter, setGameFilter] = useState<string>('all');
  const [sportFilter, setSportFilter] = useState<string>('all');

  const { data: tenants = [] } = useGetTenantsQuery();
  const { data: pickems = [], isLoading } = useGetLivePickemsQuery();
  const { canAccessGameType, allowedGameTypes } = useCurrentAdminUser();
  const visibleGameTypes = GAME_TYPES.filter(
    gt => isGameTypeEnabled(gt.code) && canAccessGameType(gt.code)
  );

  const getTenantName = useMemo(
    () => (tenantId?: string) => {
      if (!tenantId) return '-';
      const tenant = tenants.find(t => t.tenant_id === tenantId);
      return tenant?.name_en || tenant?.name_fr || tenant?.title || '-';
    },
    [tenants]
  );

  const formattedPickems = useMemo(() => {
    const mapped = pickems.map((pickem: LivePickem) => ({
      ...pickem,
      id: pickem.pickem_id ? pickem.pickem_id.toString() : '',
      title: pickem.title_en || pickem.title_fr || 'Untitled',
      tenant: getTenantName(pickem.tenant_id),
      startDate: pickem.start || '',
      game: pickem.game_type || '',
      sports: pickem.sport ? [pickem.sport.toUpperCase()] : [],
      status: 'Live' as const,
      entryCount: pickem.entry_count || 0,
    }));
    // Filter by allowed game types for member role
    if (allowedGameTypes) {
      return mapped.filter(g => allowedGameTypes.includes(g.game));
    }
    return mapped;
  }, [pickems, getTenantName, allowedGameTypes]);

  const filteredGames = useMemo(() => {
    return formattedPickems.filter((game: any) => {
      if (tenantFilter !== 'all' && game.tenant !== tenantFilter) return false;
      if (gameFilter !== 'all' && game.game !== gameFilter) return false;
      if (sportFilter !== 'all' && !game.sports.includes(sportFilter as string)) return false;
      return true;
    });
  }, [formattedPickems, tenantFilter, gameFilter, sportFilter]);

  const requirePickemId = (pickem: LivePickem): pickem is LivePickem & { pickem_id: string } => {
    if (!pickem.pickem_id) {
      toast.error('Contest ID is missing — cannot navigate to this contest');
      return false;
    }
    return true;
  };

  const handleEdit = (pickem: LivePickem) => {
    if (!requirePickemId(pickem)) return;
    switch (pickem.game_type) {
      case 'pickem':
        navigate({
          to: '/dashboard/pickems/$id',
          params: { id: pickem.pickem_id },
          search: { tenant_id: undefined, tab: undefined },
        });
        break;
      case 'squares':
        navigate({
          to: '/dashboard/squares/$id',
          params: { id: pickem.pickem_id },
          search: { tenant_id: undefined, tab: undefined },
        });
        break;
      case 'daily_reveal':
        navigate({
          to: '/dashboard/daily-reveal/$id',
          params: { id: pickem.pickem_id },
          search: { tab: 'basic' },
        });
        break;
    }
  };

  const handleScoringMode = (pickem: LivePickem) => {
    if (!requirePickemId(pickem)) return;
    switch (pickem.game_type) {
      case 'pickem':
        navigate({
          to: '/dashboard/live-scoring/$pickemId',
          params: { pickemId: pickem.pickem_id },
          search: { tab: 'scoring-mode' },
        });
        break;
      case 'squares':
        navigate({
          to: '/dashboard/squares/$id',
          params: { id: pickem.pickem_id },
          search: { tenant_id: undefined, tab: 'scoring' },
        });
        break;
      case 'daily_reveal':
        navigate({
          to: '/dashboard/daily-reveal/$id',
          params: { id: pickem.pickem_id },
          search: { tab: 'scoring' },
        });
        break;
    }
  };

  const handleLeaderboard = (pickem: LivePickem) => {
    if (!requirePickemId(pickem)) return;
    switch (pickem.game_type) {
      case 'pickem':
        navigate({
          to: '/dashboard/pickems/$id',
          params: { id: pickem.pickem_id },
          search: { tenant_id: undefined, tab: 'leaderboard' },
        });
        break;
      case 'daily_reveal':
        navigate({
          to: '/dashboard/daily-reveal/$id',
          params: { id: pickem.pickem_id },
          search: { tab: 'entries' },
        });
        break;
      case 'squares':
        navigate({
          to: '/dashboard/squares/$id',
          params: { id: pickem.pickem_id },
          search: { tenant_id: undefined, tab: 'entries' },
        });
        break;
    }
  };

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ 'live-scoring': 'Live Scoring' }} />

      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon name="GameController" size={20} />
          <h1 className="text-2xl font-semibold">Live Scoring</h1>
        </div>

        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Tenants" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map(tenant => (
              <SelectItem key={tenant.tenant_id} value={getTenantName(tenant.tenant_id)}>
                {getTenantName(tenant.tenant_id)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={gameFilter} onValueChange={setGameFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Games" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Games</SelectItem>
            {visibleGameTypes.map(gt => (
              <SelectItem key={gt.code} value={gt.code}>
                {gt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sportFilter} onValueChange={setSportFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Sports" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sports</SelectItem>
            {ENABLED_SPORTS.map(league => (
              <SelectItem key={league.code} value={league.code}>
                {league.filterLabel || league.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ContentCard>
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow>
              <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-16">
                ID
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Title</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Tenant</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Start Date</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Game</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Sports</TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                Total Entries
              </TableHead>
              <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Status</TableHead>
              <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-8 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredGames.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="GameController" size={48} className="text-gray-400" />
                    <p className="text-gray-600 dark:text-gray-400">No games found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredGames.map((game: any, index: number) => {
                // Find the original pickem by matching pickem_id
                const pickem = pickems.find((p: LivePickem) => p.pickem_id === game.pickem_id);
                const pickemId = game.pickem_id || game.id;
                return (
                  <TableRow key={`${game.id}-${index}`}>
                    <TableCell className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block max-w-[120px] truncate">{pickemId}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{pickemId}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="font-medium">{game.title}</TableCell>
                    <TableCell>{game.tenant}</TableCell>
                    <TableCell>
                      {game.startDate ? (
                        <TzDateDisplay
                          utcDate={game.startDate}
                          showTimezoneTooltip={false}
                          showUtcBadge={false}
                          formatString="MMM d, yyyy h:mm a"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{getGameTypeLabel(game.game)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {game.sports.map((sport: string) => (
                          <img
                            key={sport}
                            src={getSportLogoUrl(sport)}
                            alt={sport}
                            className="h-6 w-6 object-contain"
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{game.entryCount}</TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {game.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => pickem && handleEdit(pickem)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => pickem && handleScoringMode(pickem)}>
                            <Target className="mr-2 h-4 w-4" />
                            Scoring Mode
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => pickem && handleLeaderboard(pickem)}>
                            <BarChart3 className="mr-2 h-4 w-4" />
                            {game.game === 'pickem' ? 'Leaderboard' : 'Entries'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </ContentCard>
    </PageContainer>
  );
}
